export interface WarehouseAllocationQuantity {
  warehouse_id: number;
  quantity: number;
  purchase_price_snapshot?: number | null;
}

export interface WarehouseStockQuantity {
  product_id: number;
  warehouse_id: number;
  available: number;
}

export interface WarehouseStockConflict extends WarehouseStockQuantity {
  requested: number;
}

export interface AutomaticWarehouseSource extends WarehouseStockQuantity {
  warehouse_name: string;
  is_target: boolean;
  has_route: boolean;
  purchase_price_snapshot?: number | null;
}

export function allocateAvailableWarehouseStock(
  required: number,
  options: AutomaticWarehouseSource[],
  assigned: ReadonlyMap<number, number>
): Array<WarehouseAllocationQuantity & { warehouse_name: string }> {
  let remaining = Math.round(required * 100) / 100;
  const allocations: Array<WarehouseAllocationQuantity & { warehouse_name: string }> = [];
  const sources = [...options].sort(
    (a, b) =>
      Number(b.is_target) - Number(a.is_target) ||
      Number(b.has_route) - Number(a.has_route) ||
      b.available - a.available ||
      a.warehouse_name.localeCompare(b.warehouse_name) ||
      a.warehouse_id - b.warehouse_id
  );
  for (const source of sources) {
    if (remaining <= 0) break;
    const available = Math.max(
      0,
      Math.round((source.available - (assigned.get(source.warehouse_id) || 0)) * 100) / 100
    );
    const quantity = Math.min(remaining, available);
    if (!Number.isFinite(quantity) || quantity <= 0) continue;
    allocations.push({
      warehouse_id: source.warehouse_id,
      warehouse_name: source.warehouse_name,
      quantity,
      purchase_price_snapshot: source.purchase_price_snapshot
    });
    remaining = Math.round((remaining - quantity) * 100) / 100;
  }
  return allocations;
}

export function sourceQuantity(rows: Array<{ quantity: number }>): number {
  return Math.round(rows.reduce((total, row) => total + Number(row.quantity || 0), 0) * 100) / 100;
}

export function isSourceDistributionComplete(
  required: number,
  warehouses: WarehouseAllocationQuantity[],
  suppliers: Array<{ provider_id: number; contract_id?: number | null; quantity: number }>
): boolean {
  return (
    required > 0 &&
    warehouses.every((row) => row.warehouse_id > 0 && row.quantity > 0) &&
    suppliers.every(
      (row) => row.provider_id > 0 && Number(row.contract_id || 0) > 0 && row.quantity > 0
    ) &&
    Math.abs(sourceQuantity([...warehouses, ...suppliers]) - required) < 0.001
  );
}

export function isLocalSaleDistribution(
  required: number,
  warehouses: WarehouseAllocationQuantity[],
  suppliers: Array<{ provider_id: number; contract_id?: number | null; quantity: number }>,
  targetWarehouseId: number
): boolean {
  return (
    suppliers.length === 0 &&
    warehouses.length > 0 &&
    warehouses.every((row) => row.warehouse_id === targetWarehouseId) &&
    isSourceDistributionComplete(required, warehouses, suppliers)
  );
}

// Repeated products in different selected order lines consume the same stock budget.
export function findWarehouseStockConflicts(
  lines: Array<{ product_id: number; warehouse_allocations: WarehouseAllocationQuantity[] }>,
  stock: WarehouseStockQuantity[]
): WarehouseStockConflict[] {
  const demand = new Map<string, WarehouseStockConflict>();
  for (const line of lines) {
    for (const row of line.warehouse_allocations) {
      const key = `${line.product_id}:${row.warehouse_id}`;
      const existing = demand.get(key);
      demand.set(key, {
        product_id: line.product_id,
        warehouse_id: row.warehouse_id,
        available:
          stock.find(
            (option) =>
              option.product_id === line.product_id && option.warehouse_id === row.warehouse_id
          )?.available || 0,
        requested: Math.round(((existing?.requested || 0) + Number(row.quantity)) * 100) / 100
      });
    }
  }
  return [...demand.values()].filter((row) => row.requested - row.available > 0.001);
}

export function sourcePurchaseTotal(
  required: number,
  warehouses: WarehouseAllocationQuantity[],
  suppliers: Array<{ quantity: number; purchase_price_snapshot?: number | null }>
): number | null {
  const rows = [...warehouses, ...suppliers];
  if (
    Math.abs(sourceQuantity(rows) - required) >= 0.001 ||
    rows.some(
      (row) =>
        row.quantity <= 0 || row.purchase_price_snapshot == null || row.purchase_price_snapshot <= 0
    )
  ) {
    return null;
  }
  return rows.reduce((sum, row) => sum + row.quantity * Number(row.purchase_price_snapshot), 0);
}
